import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { RefreshCw, Check, X, Globe } from "lucide-react";
import { toast } from "sonner";

const Q_BADGE = { needs_review: "warning", approved: "success", rejected: "outline", fetch_failed: "destructive", extract_failed: "destructive" };
const Q_LABEL = { needs_review: "Needs review", approved: "Approved", rejected: "Rejected", fetch_failed: "Fetch failed", extract_failed: "Extract failed" };

export default function GrantRefreshQueue({ grants, onCatalogueChanged }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const pollRef = useRef(null);

  const loadQueue = async () => {
    try { const { data } = await api.get("/grants/refresh-queue"); setItems(data.items); } catch (e) { /* silent */ }
  };
  useEffect(() => { loadQueue(); return () => clearInterval(pollRef.current); }, []);

  const pollFor = (seconds) => {
    clearInterval(pollRef.current);
    let n = 0;
    pollRef.current = setInterval(() => { loadQueue(); if (++n >= seconds / 4) clearInterval(pollRef.current); }, 4000);
  };

  const refreshAll = async () => {
    setBusy("all");
    try {
      const { data } = await api.post("/grants/catalogue/refresh-all");
      toast.success(`Refresh started for ${data.grants} grants — results will appear in the review queue`);
      pollFor(60);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const refreshOne = async (g) => {
    setBusy(g.id);
    try {
      await api.post(`/grants/catalogue/${g.id}/refresh`);
      toast.success(`${g.code} checked against the official source`);
      loadQueue();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const decide = async (item, action) => {
    setBusy(item.id);
    try {
      await api.post(`/grants/refresh-queue/${item.id}/decision`, { action });
      toast.success(action === "approve" ? "Update applied to the catalogue" : "Proposed update rejected");
      loadQueue(); onCatalogueChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="grant-refresh-queue">
      <div className="card-flat p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold">Catalogue refresh — official sources</h3></div>
            <p className="text-[11px] text-muted-foreground mt-1">Fetches each grant's official page, AI-extracts the current terms and stages any differences here for review. Nothing changes in the catalogue until a reviewer approves.</p>
          </div>
          <Button size="sm" variant="primary" onClick={refreshAll} disabled={busy === "all"} data-testid="refresh-all-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy === "all" ? "animate-spin" : ""}`} /> Refresh all from portals
          </Button>
        </div>
        <div className="mt-3 divide-y divide-border/50">
          {grants.map((g) => (
            <div key={g.id} className="flex items-center gap-3 py-2" data-testid={`refresh-row-${g.code}`}>
              <div className="flex-1 min-w-0 text-xs">
                <span className="font-semibold">{g.code}</span> · {g.agency}
                <span className="text-muted-foreground"> · verified {g.last_verified_date || "—"}</span>
              </div>
              <Button size="sm" variant="outline" onClick={() => refreshOne(g)} disabled={busy === g.id} data-testid={`refresh-grant-${g.code}`}>
                <RefreshCw className={`h-3 w-3 mr-1 ${busy === g.id ? "animate-spin" : ""}`} />{busy === g.id ? "Checking…" : "Refresh"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-sm">Review queue ({items.filter((i) => i.status === "needs_review").length} pending)</h3>
          <Button size="sm" variant="outline" onClick={loadQueue} data-testid="reload-queue">Reload</Button>
        </div>
        {items.length === 0 && <div className="card-flat p-6 text-center text-sm text-muted-foreground" data-testid="queue-empty">No refresh runs yet — start one above.</div>}
        {items.map((item) => (
          <div key={item.id} className="card-flat p-4" data-testid={`queue-item-${item.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-semibold text-sm">{item.name}</span>
              <Badge variant={Q_BADGE[item.status] || "outline"} className="text-[9px] uppercase" data-testid={`queue-status-${item.id}`}>{Q_LABEL[item.status] || item.status}</Badge>
              {item.confidence && <Badge variant="outline" className="text-[9px]">AI confidence: {item.confidence}</Badge>}
              <span className="text-[10px] text-muted-foreground ml-auto">{new Date(item.created_at).toLocaleString()} · {item.created_by}</span>
            </div>
            {item.error && <div className="text-xs text-red-400 mt-1">{item.error}</div>}
            {item.status !== "fetch_failed" && item.status !== "extract_failed" && (
              (item.proposed_changes || []).length === 0 ? (
                <div className="text-xs text-muted-foreground mt-2">No differences detected against the official page — approving simply bumps the verified date.</div>
              ) : (
                <table className="w-full text-xs mt-2">
                  <thead><tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3">Field</th><th className="py-1.5 pr-3">Current</th><th className="py-1.5">Proposed</th>
                  </tr></thead>
                  <tbody>
                    {item.proposed_changes.map((c) => (
                      <tr key={c.field} className="border-b border-border/40">
                        <td className="py-1.5 pr-3 font-mono">{c.field}</td>
                        <td className="py-1.5 pr-3 text-muted-foreground">{String(c.current ?? "—").slice(0, 120)}</td>
                        <td className="py-1.5 text-amber-400">{String(c.proposed ?? "—").slice(0, 120)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
            {item.status === "needs_review" && (
              <div className="flex justify-end gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => decide(item, "reject")} disabled={busy === item.id} data-testid={`reject-${item.id}`}><X className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                <Button size="sm" variant="primary" onClick={() => decide(item, "approve")} disabled={busy === item.id} data-testid={`approve-${item.id}`}><Check className="h-3.5 w-3.5 mr-1" /> Approve & apply</Button>
              </div>
            )}
            {item.decided_by && <div className="text-[10px] text-muted-foreground mt-2">Decided by {item.decided_by} · {new Date(item.decided_at).toLocaleString()}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
